"use client";

/**
 * P4-02 · Mini-cart drawer.
 *
 * A right-anchored `Dialog` (focus-trap, Esc-close, focus-return come free) that
 * confirms "you added it" and nudges to checkout (P4-DB01). Reads the
 * server-authoritative `CartView` from the cart store and renders it verbatim —
 * header + free-ship progress, an independently-scrolling line list, and a
 * pinned footer. Qty/remove round-trip to the server (stepper disables with an
 * inline spinner — no optimistic drift); a live region announces total changes.
 */
import * as React from "react";

import { CartLineItem } from "~/components/cart/cart-line-item";
import { mutationErrorMessage } from "~/lib/errors/mutation-error";
import { CrossSellSlot } from "~/components/cart/cross-sell-slot";
import { Dialog } from "~/components/ui/dialog";
import { useToast } from "~/components/ui/toast";
import { CART_PATH, CHECKOUT_PATH } from "~/config/storefront";
import { LocaleLink, useTranslator } from "~/i18n/provider";
import { useUndoableCartRemove } from "~/lib/cart/use-undoable-remove";
import { useCart } from "~/lib/cart/use-cart";
import { formatMoney } from "~/lib/money";
import type { CartSummary } from "~/lib/cart/summary";
import { cn } from "~/lib/utils";

export function MiniCart() {
  const { isOpen, close, lines, summary, count, isLoading, updateQuantity } =
    useCart();
  const { toast } = useToast();
  const t = useTranslator();
  const removeLine = useUndoableCartRemove();
  const [pendingId, setPendingId] = React.useState<string | null>(null);

  const runLineMutation = async (
    itemId: string,
    fn: () => Promise<unknown>,
  ) => {
    setPendingId(itemId);
    try {
      await fn();
    } catch (err) {
      toast({
        title: mutationErrorMessage(err, "Couldn't update your cart"),
        tone: "danger",
      });
    } finally {
      setPendingId(null);
    }
  };

  const isEmpty = !isLoading && lines.length === 0;

  React.useEffect(() => {}, [isOpen]);

  return (
    <Dialog
      open={isOpen}
      onOpenChange={(o) => {
        if (!o) close();
      }}
      placement="right"
      ariaLabel={t("cart.title")}
      className="flex h-[100dvh] max-h-none w-full flex-col rounded-none border-y-0 border-r-0 p-0 sm:max-w-sm"
    >
      {/* Header */}
      <div className="flex items-center justify-between border-b border-border px-4 py-3">
        <h2 className="font-heading text-lg font-semibold text-fg">
          {t("cart.title")}{" "}
          <span className="font-normal text-fg-muted">
            · {t("cart.itemCount", { count })}
          </span>
        </h2>
        <button
          type="button"
          aria-label={t("cart.closeCart")}
          onClick={close}
          className="rounded-md p-1.5 text-fg-muted hover:bg-surface-2 hover:text-fg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
        >
          <span aria-hidden="true">×</span>
        </button>
      </div>

      {/* Free-shipping progress (only when a threshold is configured) */}
      {summary && summary.freeShippingThresholdMinor != null ? (
        <FreeShippingProgress summary={summary} />
      ) : null}

      {/* Line list — the independently scrolling region */}
      <div className="flex-1 overflow-y-auto px-4">
        {isLoading && lines.length === 0 ? (
          <MiniCartSkeleton />
        ) : isEmpty ? (
          <EmptyMiniCart onClose={close} />
        ) : (
          <>
            {lines.map((line) => (
              <CartLineItem
                key={line.id}
                line={line}
                pending={pendingId === line.id}
                onQuantityChange={(q) =>
                  runLineMutation(line.id, () => updateQuantity(line.id, q))
                }
                onRemove={() =>
                  void runLineMutation(line.id, () => removeLine(line))
                }
              />
            ))}
            <CrossSellSlot surface="mini" enabled={lines.length > 0} />
          </>
        )}
      </div>

      {/* Footer — only with items */}
      {summary && lines.length > 0 ? (
        <MiniCartFooter summary={summary} onNavigate={close} />
      ) : null}

      {/* Screen-reader total announcement */}
      <div aria-live="polite" aria-atomic="true" className="sr-only">
        {summary && lines.length > 0
          ? `Cart total ${formatMoney(summary.grandTotalMinor, summary.currency)}, ${t("cart.itemCount", { count })}`
          : ""}
      </div>
    </Dialog>
  );
}

function FreeShippingProgress({ summary }: { summary: CartSummary }) {
  const threshold = summary.freeShippingThresholdMinor!;
  const remaining = summary.freeShippingRemainingMinor;
  const unlocked = remaining == null;
  const pct = unlocked
    ? 100
    : Math.max(
        0,
        Math.min(100, Math.round(((threshold - remaining) / threshold) * 100)),
      );

  return (
    <div className="border-b border-border bg-surface-2/50 px-4 py-2.5">
      <p
        className={cn(
          "mb-1.5 text-xs font-semibold",
          unlocked ? "text-success" : "text-fg",
        )}
      >
        {unlocked
          ? "✓ You’ve unlocked free shipping"
          : `Add ${formatMoney(remaining, summary.currency)} more to unlock free shipping`}
      </p>
      <div className="h-1.5 overflow-hidden rounded-full bg-border">
        <div
          className={cn(
            "h-full rounded-full",
            unlocked ? "bg-success" : "bg-secondary",
          )}
          style={{ width: `${pct}%` }}
        />
      </div>
    </div>
  );
}

function MiniCartFooter({
  summary,
  onNavigate,
}: {
  summary: CartSummary;
  onNavigate: () => void;
}) {
  const t = useTranslator();
  const savings = summary.discountTotalMinor + summary.giftCardMinor;
  return (
    <div className="border-t border-border px-4 py-3">
      <div className="flex justify-between text-sm">
        <span className="text-fg-muted">{t("cart.subtotal")}</span>
        <span className="font-semibold text-fg tabular-nums">
          {formatMoney(summary.subtotalMinor, summary.currency)}
        </span>
      </div>
      {savings > 0 ? (
        <div className="mt-1 flex justify-between text-sm text-success">
          <span>{t("cart.savingsApplied")}</span>
          <span className="tabular-nums">
            −{formatMoney(savings, summary.currency)}
          </span>
        </div>
      ) : null}
      <p className="mt-1.5 text-xs text-fg-muted">
        {t("cart.shippingTaxNote")}
      </p>
      <LocaleLink
        href={CHECKOUT_PATH}
        onClick={() => {
          onNavigate();
        }}
        className="mt-3 block rounded-[var(--btn-radius)] bg-primary px-4 py-2.5 text-center text-sm text-primary-fg [font-weight:var(--btn-weight)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        {t("cart.checkout")}
      </LocaleLink>
      <LocaleLink
        href={CART_PATH}
        onClick={onNavigate}
        className="mt-2 block text-center text-sm text-secondary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        {t("cart.viewCart")}
      </LocaleLink>
    </div>
  );
}

function EmptyMiniCart({ onClose }: { onClose: () => void }) {
  const t = useTranslator();
  return (
    <div className="flex flex-col items-center px-4 py-12 text-center">
      <div
        aria-hidden="true"
        className="mb-3 flex size-12 items-center justify-center rounded-xl bg-surface-2 text-2xl"
      >
        🛍
      </div>
      <p className="text-sm font-semibold text-fg">{t("cart.empty")}</p>
      <p className="mt-1 max-w-56 text-xs text-fg-muted">
        {t("cart.miniEmpty")}
      </p>
      <LocaleLink
        href="/"
        onClick={onClose}
        className="mt-4 rounded-[var(--btn-radius)] bg-primary px-4 py-2 text-sm text-primary-fg [font-weight:var(--btn-weight)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--ring)]"
      >
        {t("cart.shopNewArrivals")}
      </LocaleLink>
    </div>
  );
}

function MiniCartSkeleton() {
  return (
    <div className="py-3" aria-hidden="true">
      {[0, 1].map((i) => (
        <div
          key={i}
          className="flex gap-3 border-b border-border py-3 last:border-b-0"
        >
          <div className="aspect-[4/5] w-14 shrink-0 animate-pulse rounded-md bg-surface-2" />
          <div className="flex-1 space-y-2 py-1">
            <div className="h-3 w-3/4 animate-pulse rounded bg-surface-2" />
            <div className="h-2.5 w-2/5 animate-pulse rounded bg-surface-2" />
            <div className="mt-3 h-8 w-3/5 animate-pulse rounded bg-surface-2" />
          </div>
        </div>
      ))}
    </div>
  );
}
